import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Table, Typography, Card as CardAnt } from 'antd';

import {
  ColSize,
  ContentSection,
  DatePickerForm,
  Form,
  InputForm,
  RowSize,
  SelectForm,
} from '../../../../../../../../../../../../../shared/components';
import {
  ObjectFormSection,
} from '../../../../../../../../../shared';

import { GenericDataFormContext, QuotationInfoContext } from '../../../../../../../../../shared/contexts';
import { StepFiveFormContext } from '../../../../contexts/StepFiveForm';
import { StepThreeFormContext } from '../../../../../../../StepThree/contexts/StepThreeForm';
import { StepTwoFormContext } from '../../../../../../../StepTwo/contexts/StepTwoForm';

import { ComponentCommerceQuotation } from '../../../../../../../../../../../../../shared/interfaces';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../../../../../shared/utils';

import './index.css';
import { COLORS } from '../../../../../../../../../../../../../config/themes/colors';
import { TransferRightsForm } from '../../../TransferRightsForm';

interface Coverage {
  codigo: string;
  description: string;
  familyCode?: string;
  familyDescription?: string;
  key: string;
  riskCode: string;
  sumInsured: string;
  typeCoverage?: string;
}

interface CoverageGroup {
  [familyCode: string]: Coverage[];
}

interface CoveragesProps {
  commerceComponent: ComponentCommerceQuotation;
}

const MOCK_TABLE_COLUMNS_HEADER = [
  {
    title: 'Descripción de cobertura',
    dataIndex: 'description',
    width: '50%',
  },
  {
    title: 'Suma asegurada',
    dataIndex: 'sumInsured',
    width: '50%',
  },
];

export const FormCoverages: FC<CoveragesProps> = ({ commerceComponent }) => {
  const validateForm = new ValidateForm();

  const [coveragesListInfo, setCoveragesListInfo] = useState<CoverageGroup[]>(
    []
  );

  const { propertyDataForm, contactDataForm } = useContext(StepFiveFormContext);
  const { provinceList, allLocalitiesPerProvince, allCoveragesPerPlan } = useContext(
    GenericDataFormContext
  );
  const { stepThreeForm } = useContext(StepThreeFormContext);
  const { rubricSelected } = useContext(StepTwoFormContext)

  const provinceValueByTab = stepThreeForm?.getFieldValue(
    `province-tab-${commerceComponent.nroDeComponente}`
  );
  const locationValueByTab = stepThreeForm?.getFieldValue(
    `location-tab-${commerceComponent.nroDeComponente}`
  );
  const localitiesPerProvince = allLocalitiesPerProvince.find(
    (loc) => String(loc.provinceCode) === String(provinceValueByTab)
  )?.localitiesOptions;

  const setValuesForm = () => {
    propertyDataForm.setFieldValue(
      `propertyDataProvince${commerceComponent.nroDeComponente}`,
      provinceValueByTab
    );
    propertyDataForm.setFieldValue(
      `propertyDataLocality${commerceComponent.nroDeComponente}`,
      locationValueByTab
    );
  };

  const propertyDataAddressValueByTab = stepThreeForm?.getFieldValue(`propertyDataAddress${commerceComponent.nroDeComponente}`);

  const setCompleteCoveragesListInfo = useCallback(() => {

    const planSelected = stepThreeForm?.getFieldValue(`plan-tab-${commerceComponent.nroDeComponente}`);

    const matchingCoverages = allCoveragesPerPlan?.find((el) => String(el.plan) === String(planSelected)) ?.coverages?.filter((coverage) => String(coverage.codigoRiesgo) === String(rubricSelected?.codigoDeRiesgo));

    if (matchingCoverages) {
      const filteredCoverages = matchingCoverages.filter(
        (planCoverage) =>
          commerceComponent.coberturas.some(
            (cov) => cov.codigoCobertura === planCoverage.codigo
          )
      );

      const coveragesListInfo = filteredCoverages.map((planCoverage) => {
        const matchedCoverage = commerceComponent.coberturas.find(
          (cov) => cov.codigoCobertura === planCoverage.codigo
        );

        let familyDescription;

        if (planCoverage?.codigoFamilia) {
          familyDescription = planCoverage.descripcionFamilia;
        } else if (planCoverage.tipoDeCobertura === 'O') {
          familyDescription = 'ADICIONAL';
        } else {
          familyDescription = 'OTRAS';
        }

        return {
          codigo: planCoverage.codigo,
          description: planCoverage.descripcionLarga,
          familyCode:
            planCoverage?.codigoFamilia ??
            (planCoverage.tipoDeCobertura === 'O'
              ? 'additional'
              : 'noFamilyCode'),
          familyDescription,
          key: planCoverage.codigo,
          riskCode: planCoverage.codigoRiesgo,
          sumInsured: `$ ${formattedPrice(
            `${matchedCoverage ? matchedCoverage.sumaAsegurada : 0}`
          )}`,
          typeCoverage: planCoverage?.tipoDeCobertura,
        };
      });

      const groupedData = coveragesListInfo.reduce<{
        [key: string]: Coverage[];
      }>((acc, item) => {
        let codeFamilyData: string;

        codeFamilyData = item.familyCode;

        if (!acc[codeFamilyData]) acc[codeFamilyData] = [];

        acc[codeFamilyData].push(item);
        return acc;
      }, {});

      const desiredOrder = ['noFamilyCode', 'additional'];
      const orderedGroupedData: { [key: string]: Coverage[] } = {};

      desiredOrder.forEach((key) => {
        if (groupedData[key]) orderedGroupedData[key] = groupedData[key];
      });

      Object.keys(groupedData)
        .filter((key) => !desiredOrder.includes(key))
        .forEach((key) => {
          orderedGroupedData[key] = groupedData[key];
        });

      setCoveragesListInfo([orderedGroupedData]);

      return;
    }

    setCoveragesListInfo([]);
  }, [allCoveragesPerPlan, stepThreeForm]);

  useEffect(() => {
    setValuesForm();
  }, [commerceComponent]);

  useEffect(() => {
    setCompleteCoveragesListInfo();
  }, [setCompleteCoveragesListInfo]);

  const { renewalQuotationState } = useContext(QuotationInfoContext);
  const isRenewal = !!renewalQuotationState;

  return (
    <CardAnt>
      <Form
        formProps={{
          form: propertyDataForm,
          labelCol: { span: 24 },
          name: `activity-form-${commerceComponent.nroDeComponente}`,
        }}
      >
        <ContentSection level='h3' title='Datos del bien'>
          <RowSize>
            <ColSize size={25}>
              <InputForm
                formProps={{
                  label: 'Domicilio:',
                  name: `propertyDataAddress${commerceComponent.nroDeComponente}`,
                  rules: validateForm.generateRules({
                    label: 'domicilio',
                    rules: ['required'],
                  }),
                  initialValue: isRenewal ? (propertyDataAddressValueByTab ?? '') : '',
                }}
                inputProps={{ placeholder: 'Senillosa 342 1B', disabled: !!propertyDataAddressValueByTab && isRenewal, }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Provincia:',
                  name: `propertyDataProvince${commerceComponent.nroDeComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  loading: provinceList?.length === 0,
                  options: provinceList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Localidad:',
                  name: `propertyDataLocality${commerceComponent.nroDeComponente}`,
                }}
                selectProps={{
                  disabled: true,
                  options: localitiesPerProvince,
                }}
              />
            </ColSize>
          </RowSize>
        </ContentSection>
      </Form>

      {commerceComponent.requiereInspeccion === 'S' && (
        <Form
          formProps={{
            form: contactDataForm,
            name: `contact-data-form-${commerceComponent.nroDeComponente}`,
          }}
        >
          <ContentSection level='h3' title='Información de Contacto'>
            <RowSize>
              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Nombre y Apellido:',
                    name: `fullNameContact${commerceComponent.nroDeComponente}`,
                    rules: validateForm.generateRules({
                      label: 'nombre y apellido',
                      rules: ['required', 'fullName'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Teléfono:',
                    name: `phoneNumberContact${commerceComponent.nroDeComponente}`,
                    rules: validateForm.generateRules({
                      label: 'teléfono',
                      rules: ['required', 'phone'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Correo Electrónico:',
                    name: `emailContact${commerceComponent.nroDeComponente}`,
                    rules: validateForm.generateRules({
                      label: 'correo electrónico',
                      rules: ['required', 'email'],
                    }),
                  }}
                />
              </ColSize>

              <ColSize size={25}>
                <DatePickerForm
                  rangePickerProps={{
                    format: 'HH:mm',
                    picker: 'time',
                    showTime: { format: 'HH:mm' },
                  }}
                  formProps={{
                    label: 'Disponibilidad Horaria:',
                    name: `hoursForContact${commerceComponent.nroDeComponente}`,
                    rules: validateForm.generateRules({
                      label: 'disponibilidad horaria',
                      rules: ['required'],
                    }),
                  }}
                  isRangePicker
                />
              </ColSize>

              <ColSize size={25}>
                <InputForm
                  formProps={{
                    label: 'Comentarios:',
                    name: `commentsContact${commerceComponent.nroDeComponente}`,
                  }}
                  inputTextAreaProps={{ rows: 1 }}
                  isTextArea
                />
              </ColSize>
            </RowSize>
          </ContentSection>
        </Form>
      )}

      <ContentSection level='h3' title='Cobertura' hasCard={false}>
        {coveragesListInfo?.map((coverageGroup: any) => {
          const familyCodes = Object.keys(coverageGroup);

          return familyCodes.map((familyCode, index) => {
            const coverages = coverageGroup[familyCode];

            return (
              <React.Fragment key={`${familyCode}-${index}`}>
                <ColSize size={100}>
                  <Typography.Title
                    level={5}
                    style={{
                      margin: 0,
                      marginBottom: 10,
                      color: COLORS.secondary[100],
                    }}
                  >
                    {coverages[0]?.familyDescription}
                  </Typography.Title>
                </ColSize>
                <Table
                  bordered
                  className='person-table'
                  columns={MOCK_TABLE_COLUMNS_HEADER}
                  dataSource={coverages}
                  pagination={false}
                  scroll={{ x: 'auto' }}
                />
              </React.Fragment>
            );
          });
        })}
      </ContentSection>

      <ObjectFormSection
        coveragesListInfo={coveragesListInfo[0]}
        numberComponent={commerceComponent.nroDeComponente}
      />
      <TransferRightsForm commerceComponent={commerceComponent} />
    </CardAnt>
  );
};
